import { GanttColumn, GanttRememberColumnState, GanttRememberState } from './gantt.types';

const GANTT_REMEMBER_VERSION = 1;

export function createGanttRememberState(state: Partial<GanttRememberState> = {}): GanttRememberState {
    return {
        version: GANTT_REMEMBER_VERSION,
        ...state,
        updatedAt: new Date().toISOString(),
    };
}

export function isGanttRememberState(value: unknown): value is GanttRememberState {
    return !!value && !Array.isArray(value) && typeof value === 'object' && (value as GanttRememberState).version === GANTT_REMEMBER_VERSION;
}

export function applyRememberedColumnState(columns: GanttColumn[], state: GanttRememberColumnState | undefined): GanttColumn[] {
    if (!state) return columns;

    const widths = state.widths ?? {};
    const hiddenPropertyKeys = new Set(state.hiddenPropertyKeys ?? []);
    const orderedPropertyKeys = state.orderedPropertyKeys ?? [];
    const columnsByKey = new Map(columns.map((column) => [getColumnPropertyKey(column), column]));

    const orderedColumns = orderedPropertyKeys
        .map((propertyKey) => columnsByKey.get(propertyKey))
        .filter((column): column is GanttColumn => !!column);

    const rememberedKeys = new Set(orderedColumns.map(getColumnPropertyKey));
    const newColumns = columns.filter((column) => !rememberedKeys.has(getColumnPropertyKey(column)));

    return [...orderedColumns, ...newColumns].map((column) => {
        const propertyKey = getColumnPropertyKey(column);
        const rememberedWidth = widths[propertyKey];

        return {
            ...column,
            width: rememberedWidth ?? column.width,
            hidden: hiddenPropertyKeys.has(propertyKey) ? true : column.hidden,
            hide: hiddenPropertyKeys.has(propertyKey) ? true : column.hide,
        };
    });
}

export function createRememberedColumnState(columns: GanttColumn[]): GanttRememberColumnState {
    const widths: Record<string, number | string> = {};
    const hiddenPropertyKeys: string[] = [];
    const orderedPropertyKeys = columns.map((column) => {
        const propertyKey = getColumnPropertyKey(column);

        if (column.width !== undefined) {
            widths[propertyKey] = column.width;
        }

        if (isBooleanLikeTrue(column.hidden ?? column.hide)) {
            hiddenPropertyKeys.push(propertyKey);
        }

        return propertyKey;
    });

    return {
        orderedPropertyKeys,
        widths,
        hiddenPropertyKeys,
    };
}

export function getColumnPropertyKey(column: Pick<GanttColumn, 'propertyKey' | 'name'>): string {
    return column.propertyKey ?? column.name ?? '';
}

function isBooleanLikeTrue(value: unknown): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
}
